// Company holidays (Betriebsferien) as the setup wizard stages them: the days
// the company itself closes, entered once at setup and written into Timecard at
// Finish (onboarding-replay.ts). The shape is time-api's `POST /company-holidays`
// body, so the staged blob is the contract between the wizard and the replay.
export interface StagedCompanyHoliday {
  name: string;
  /** YYYY-MM-DD. */
  startDate: string;
  /** YYYY-MM-DD; the start date again for a single day. */
  endDate: string;
  isHalfDay: boolean;
  /** Repeats on the same dates every year (24 December), or happens once. */
  recursYearly: boolean;
  /** Taken from everybody's holiday entitlement, or granted on top of it. */
  deductsFromLeave: boolean;
}

const YMD = /^\d{4}-\d{2}-\d{2}$/;
const MAX_NAME_LENGTH = 120;

/** What makes two entries the same one: their name and the day they start. */
export function companyHolidayKey(entry: { name: string; startDate: string }): string {
  return `${entry.name.trim().toLowerCase()}|${String(entry.startDate).slice(0, 10)}`;
}

/**
 * The staged list, read defensively: a blob written by an older build or edited
 * by hand may hold anything. An entry that cannot be a company holiday is
 * dropped rather than sent — one bad row must not 400 the whole replay and
 * strand the commit on something the admin cannot see, let alone fix.
 */
export function normalizeCompanyHolidays(value: unknown): StagedCompanyHoliday[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: StagedCompanyHoliday[] = [];
  for (const raw of value) {
    const name = typeof raw?.name === 'string' ? raw.name.trim().slice(0, MAX_NAME_LENGTH) : '';
    const startDate = typeof raw?.startDate === 'string' ? raw.startDate : '';
    const endDate = typeof raw?.endDate === 'string' && raw.endDate ? raw.endDate : startDate;
    if (!name || !YMD.test(startDate) || !YMD.test(endDate) || endDate < startDate) continue;
    const entry: StagedCompanyHoliday = {
      name,
      startDate,
      endDate,
      isHalfDay: raw.isHalfDay === true,
      recursYearly: raw.recursYearly === true,
      deductsFromLeave: raw.deductsFromLeave === true,
    };
    const key = companyHolidayKey(entry);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
}
